/**
 * Reusable Error Display Component
 * 
 * Issue #7: Inconsistent Error Handling Across Features
 * Issue #9: Poor User Feedback on Errors
 * 
 * Provides consistent error display with:
 * - Severity-based styling
 * - Recovery suggestions
 * - Retry actions
 * - Dismissible alerts
 */

import React from 'react';
import { AlertCircle, AlertTriangle, Info, XCircle, X } from 'lucide-react';
import { AppError, ErrorSeverity } from '../../../utils/errorHandler';

interface ErrorDisplayProps {
  error: AppError | null;
  onDismiss?: () => void;
  onRetry?: () => void;
  className?: string;
  showSuggestions?: boolean;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onDismiss,
  onRetry,
  className = '',
  showSuggestions = true,
}) => {
  if (!error) return null;

  // Determine styling based on severity
  const getSeverityStyles = () => {
    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
        return {
          container: 'bg-red-50 border-red-300 text-red-900',
          icon: 'text-red-600',
          IconComponent: XCircle,
        };
      case ErrorSeverity.HIGH:
        return {
          container: 'bg-orange-50 border-orange-300 text-orange-900',
          icon: 'text-orange-600',
          IconComponent: AlertTriangle,
        };
      case ErrorSeverity.MEDIUM:
        return {
          container: 'bg-yellow-50 border-yellow-300 text-yellow-900',
          icon: 'text-yellow-600',
          IconComponent: AlertCircle,
        };
      case ErrorSeverity.LOW:
        return {
          container: 'bg-blue-50 border-blue-300 text-blue-900',
          icon: 'text-blue-600',
          IconComponent: Info,
        };
      default:
        return {
          container: 'bg-gray-50 border-gray-300 text-gray-900',
          icon: 'text-gray-600',
          IconComponent: AlertCircle,
        };
    }
  };

  const styles = getSeverityStyles();
  const { IconComponent } = styles;

  return (
    <div
      className={`border rounded-md p-4 ${styles.container} ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <IconComponent className={`h-5 w-5 ${styles.icon}`} aria-hidden="true" />
        </div>
        
        <div className="ml-3 flex-1">
          {/* Error message */}
          <h3 className="text-sm font-medium">
            {error.userMessage}
          </h3>

          {/* Recovery suggestions */}
          {showSuggestions && error.recoverySuggestions && error.recoverySuggestions.length > 0 && (
            <div className="mt-2 text-sm">
              <p className="font-medium mb-1">Suggestions:</p>
              <ul className="list-disc list-inside space-y-1">
                {error.recoverySuggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Action buttons */}
          {(onRetry || onDismiss) && (
            <div className="mt-3 flex gap-2">
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>

        {/* Dismiss icon */}
        {onDismiss && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={onDismiss}
              className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.icon} hover:bg-white/50`}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Inline error display for form fields
 */
interface InlineErrorProps {
  message: string;
  className?: string;
}

export const InlineError: React.FC<InlineErrorProps> = ({ message, className = '' }) => {
  if (!message) return null;

  return (
    <div className={`flex items-center gap-1 text-sm text-red-600 mt-1 ${className}`} role="alert">
      <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
};

/**
 * Toast-style error notification (for non-blocking errors)
 */
interface ErrorToastProps {
  error: AppError | null;
  onDismiss: () => void;
  duration?: number; // Auto-dismiss after duration (ms)
}

export const ErrorToast: React.FC<ErrorToastProps> = ({
  error,
  onDismiss,
  duration = 5000,
}) => {
  React.useEffect(() => {
    if (error && duration > 0) {
      const timer = setTimeout(() => {
        onDismiss();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [error, duration, onDismiss]);

  if (!error) return null;

  const getSeverityColor = () => {
    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        return 'bg-red-600';
      case ErrorSeverity.MEDIUM:
        return 'bg-yellow-600';
      case ErrorSeverity.LOW:
        return 'bg-blue-600';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <div
      className={`fixed bottom-4 right-4 max-w-sm ${getSeverityColor()} text-white rounded-lg shadow-lg p-4 flex items-start gap-3 animate-slide-up z-50`}
      role="alert"
      aria-live="assertive"
    >
      <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1">
        <p className="text-sm font-medium">{error.userMessage}</p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="flex-shrink-0 inline-flex text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white rounded"
        aria-label="Dismiss"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
};

